import moment, { Moment } from 'moment';
import { createSelector } from 'reselect';
import { CalendarEvent } from 'shovel-lib/types';
import { RootState } from '@state/types';
import { SourceEvent, SourceEventType } from './types';
import * as actions from './actions';
import { getType } from 'typesafe-actions';
import unpackEvent from '@utils/unpackEvent';
import { captureException, captureMessage } from '@sentry/react';

const safeUnpackEvent = (event: SourceEvent, holidays: CalendarEvent[] = [], start?: Date, end?: Date): CalendarEvent[] => {
  try {
    return unpackEvent(event, holidays, start, end);
  } catch (e) {
    const message = 'Failed unpacking event ' + event.id;
    console.log(message);
    console.log(e);
    console.log(event);
    captureException(e);
    captureMessage(message);
    return [];
  }
};

type EventRange = {
  start?: Moment | Date | string | null;
  end?: Moment | Date | string | null;
};

const getRange = (state: RootState, range: EventRange) => {
  const start = range?.start || state.event.start;
  const end = range?.end || state.event.end;

  return { start: start ? moment(start).toDate() : null, end: end ? moment(end).toDate() : null };
};

const separateAndUnpackHolidays = (sourceEvents: SourceEvent[], start: Date, end: Date) => {
  const { holidays, events } = sourceEvents.reduce(
    ({ holidays, events }, event) =>
      event.type === SourceEventType.HOLIDAY
        ? { holidays: [...holidays, ...safeUnpackEvent(event, [], start, end)], events }
        : { holidays, events: [...events, event] },
    { holidays: [], events: [] } as { holidays: (CalendarEvent | SourceEvent)[]; events: (CalendarEvent | SourceEvent)[] }
  );

  return [holidays, events];
};

type NonStateEvents = {
  nonStateEvents?: SourceEvent[];
};

const getNonStateEvents = (state: RootState, props: NonStateEvents) => props.nonStateEvents;

/**
 * The events of `sourceEvents` (repeating ones as each time they happen) between `start` and `end`. A plain function
 * too, for a range other than the dates on screen: the timer looks a week ahead whatever the calendar shows (bump.ts).
 */
export const unpackSourceEvents = (sourceEvents: SourceEvent[], start: Date, end: Date): CalendarEvent[] => {
  const [holidays, events] = separateAndUnpackHolidays(sourceEvents, start, end);
  return [
    ...(holidays as CalendarEvent[]),
    // @ts-ignore
    ...events.reduce((dtos, event) => [...dtos, ...safeUnpackEvent(event, holidays, start, end)], [])
  ];
};

const unpackEventsInRange = createSelector(
  [(state: RootState) => state.event.sourceEvents, getRange, getNonStateEvents],
  (_sourceEvents = [], { start, end }, nonStateEvents = []) => {
    if (!start || !end) {
      return [];
    }
    return unpackSourceEvents([..._sourceEvents, ...nonStateEvents], start, end);
  }
);

const getSourceEvents = (state: RootState) => state.event.sourceEvents;

/** The dates whose events are unpacked (the dates on screen) as a key, so the calendar opens each of them once. */
const getUnpackedDates = ({ event: { start, end } }: RootState) =>
  start && end ? `${start.valueOf()}-${end.valueOf()}` : undefined;

const sourceEventsLoading = (state: RootState) => {
  const sourceEventsType = getType(actions.sourceEvents.request).split('_REQUEST')[0];
  const createEventType = getType(actions.createEvent.request).split('_REQUEST')[0];
  return !!state.common.loaders[sourceEventsType] || !!state.common.loaders[createEventType];
};

export { unpackEventsInRange, sourceEventsLoading, getSourceEvents, getUnpackedDates };
